import {
  Provider as JotaiProvider,
  type SetStateAction,
  atom,
  createStore,
  useAtom,
  useAtomValue,
} from "jotai"
import { type ReactNode, useRef } from "react"
import { type MartId } from "types/ApiTypes"
import type {
  ColDef,
  ColGroupDef,
  DataGridState,
  ExcelExportParams,
  CsvExportParams,
  DataTableProps,
} from "@tokenterminal/ui/DataTable"

export type ExportType = "excel" | "csv"
export type ExportParams = Pick<
  ExcelExportParams | CsvExportParams,
  "fileName" | "processCellCallback"
>
export type Exporter<T extends Record<string, unknown>> = (
  p: Pick<DataTableProps<T>, "data" | "columnDefs">,
  callback: () => void
) => void

type DataGridContextState<T extends Record<string, unknown>> = {
  // Get current state from grid to be saved as a preset or updated
  getState: () => undefined | DataGridState
  // Download actions
  downloadCsv: () => void
  downloadExcel: () => void
  // Full export
  createExporter: (
    type: ExportType,
    params: ExportParams
  ) => Promise<Exporter<T>>
  // Expose columns to context for downloading
  getColDefs: () => undefined | Array<ColDef<T, any> | ColGroupDef<T>>
  // Are there things to be saved as custom
  dirty: boolean
  // Id of the mart/dataset, required for saving/updating
  martId: MartId
  // User preset or Admin UI defined preset. ID available via query param as well
  preset?: null | {
    id: string
    name: string
    type: "user" | "tokenterminal"
  }
}

const voidFn = () => {}

const initialStaticState: Omit<DataGridContextState<any>, "martId"> = {
  getState: () => undefined,
  downloadCsv: voidFn,
  downloadExcel: voidFn,
  createExporter: async () => voidFn,
  getColDefs: () => undefined,
  dirty: false,
}

// Static initial state has no martId, but we always initialize the context with it via the provider so casting should be fine
const chartActionsAtom = atom(initialStaticState as DataGridContextState<any>)

interface InitialConfig {
  id: MartId
}

export function DataGridProvider({
  children,
  id,
}: { children: ReactNode } & InitialConfig) {
  const storeRef = useRef<ReturnType<typeof createStore>>()
  if (!storeRef.current) {
    storeRef.current = createStore()

    // Initialize as DataGridContextState
    storeRef.current.set(chartActionsAtom, (s) => ({ ...s, martId: id }))
  }

  return <JotaiProvider store={storeRef.current}>{children}</JotaiProvider>
}

export const useDataGrid = () => useAtomValue(chartActionsAtom)
export const useDataGridState = <T extends Record<string, unknown>>() =>
  useAtom<
    DataGridContextState<T>,
    [SetStateAction<DataGridContextState<T>>],
    void
  >(chartActionsAtom)
